import { FormEvent, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useAuth } from '../lib/auth';
import { Alert, Badge, Button, EmptyState, Field, Input, Loading, Modal } from '../components/ui';
import { IndustryEditor } from '../components/IndustryEditor';

const TABS = ['Targeting', 'Research', 'Disqualifiers', 'Compliance', 'Preferences'] as const;
type Tab = (typeof TABS)[number];

export default function Settings() {
  const [tab, setTab] = useState<Tab>('Targeting');
  return (
    <div className="space-y-6">
      <h1 className="h-display text-3xl text-soft">Settings</h1>
      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 font-head text-sm font-semibold ${tab === t ? 'border-b-2 border-power text-soft' : 'text-muted hover:text-soft'}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'Targeting' && <Targeting />}
      {tab === 'Research' && <ResearchRubrics />}
      {tab === 'Disqualifiers' && <Disqualifiers />}
      {tab === 'Compliance' && <Compliance />}
      {tab === 'Preferences' && <Preferences />}
    </div>
  );
}

/* ---------- Targeting profiles (Task 5: multiple ICPs per group) ---------- */
function Targeting() {
  const { me } = useAuth();
  const canEdit = me?.role === 'owner' || me?.role === 'manager';
  const { data, loading, reload } = useAsync(() => apiGet<{ profiles: any[] }>('/api/settings/profiles'), []);
  const [showAdd, setShowAdd] = useState(false);

  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Run different targeting criteria per segment — a company that sells into manufacturing AND
        government keeps a profile for each.
      </p>
      <div className="space-y-3">
        {(data?.profiles || []).map((p) => (
          <div key={p.id} className="card px-4 py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-head font-semibold text-soft">{p.name}</span>
                  {p.isDefault && <Badge className="border-victory/40 text-victory">default</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-muted">
                  {p.sellTo || '—'} · {p.geoScope || '—'} · sourcing: {p.sourcingMode}
                </div>
              </div>
              {canEdit && !p.isDefault && (
                <>
                  <Button variant="ghost" className="px-3 py-1.5 text-sm" onClick={async () => { await apiPatch(`/api/settings/profiles/${p.id}`, { makeDefault: true }); reload(); }}>Make default</Button>
                  <Button variant="danger" className="px-3 py-1.5 text-sm" onClick={async () => { await apiDelete(`/api/settings/profiles/${p.id}`); reload(); }}>Delete</Button>
                </>
              )}
            </div>
            <div className="mt-3 border-t border-line pt-3">
              <div className="mb-1.5 font-head text-sm font-semibold text-soft/90">Industries / what you sell</div>
              <IndustryEditor
                value={p.targetIndustries || []}
                disabled={!canEdit}
                onChange={(next) => { apiPatch(`/api/settings/profiles/${p.id}`, { targetIndustries: next }); }}
              />
            </div>
          </div>
        ))}
      </div>
      {canEdit && <Button onClick={() => setShowAdd(true)}>+ Add targeting profile</Button>}
      <AddProfile open={showAdd} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); reload(); }} />
    </div>
  );
}

function AddProfile({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: '', sellTo: 'b2b', geoScope: 'national', targetIndustries: '', idealCustomerDesc: '' });
  const [busy, setBusy] = useState(false);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost('/api/settings/profiles', {
        ...f, targetIndustries: f.targetIndustries.split(',').map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } finally { setBusy(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="New targeting profile">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Name"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Government / Military" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sell to">
            <select className="input" value={f.sellTo} onChange={(e) => set({ sellTo: e.target.value })}>
              <option value="consumers">Individual people</option>
              <option value="local_business">Nearby businesses</option>
              <option value="b2b">Companies (B2B)</option>
              <option value="government">Government</option>
            </select>
          </Field>
          <Field label="Geography">
            <select className="input" value={f.geoScope} onChange={(e) => set({ geoScope: e.target.value })}>
              <option value="hyperlocal">Hyper-local</option>
              <option value="regional">Regional</option>
              <option value="national">National</option>
              <option value="none">No constraint</option>
            </select>
          </Field>
        </div>
        <Field label="Target industries" hint="Comma-separated."><Input value={f.targetIndustries} onChange={(e) => set({ targetIndustries: e.target.value })} /></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" loading={busy}>Add</Button></div>
      </form>
    </Modal>
  );
}

/* ---------- Disqualifiers ---------- */
function Disqualifiers() {
  const { me } = useAuth();
  const canEdit = me?.role === 'owner' || me?.role === 'manager';
  const { data, loading, reload } = useAsync(() => apiGet<{ disqualifiers: any[] }>('/api/settings/disqualifiers'), []);
  const [value, setValue] = useState('');
  const [kind, setKind] = useState('competitor');
  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">Who to actively exclude — competitors, do-not-contact, wrong-size. Sourcing and sends honor this.</p>
      {canEdit && (
        <form onSubmit={async (e) => { e.preventDefault(); if (!value.trim()) return; await apiPost('/api/settings/disqualifiers', { kind, value }); setValue(''); reload(); }}
          className="card flex flex-wrap items-end gap-3 p-4">
          <Field label="Type"><select className="input" value={kind} onChange={(e) => setKind(e.target.value)}><option value="competitor">Competitor</option><option value="do_not_contact">Do not contact</option><option value="company_size">Wrong size</option><option value="other">Other</option></select></Field>
          <div className="flex-1"><Field label="Value"><Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Acme Corp" /></Field></div>
          <Button type="submit">Add</Button>
        </form>
      )}
      {(data?.disqualifiers || []).length === 0 ? (
        <EmptyState title="No disqualifiers" body="Add competitors or do-not-contact names to keep them out of your outreach." />
      ) : (
        <div className="card divide-y divide-line">
          {data!.disqualifiers.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-4 py-2.5">
              <Badge className="border-line text-muted">{d.kind}</Badge>
              <span className="flex-1 text-sm text-soft">{d.value}</span>
              {canEdit && <button className="text-xs text-danger hover:underline" onClick={async () => { await apiDelete(`/api/settings/disqualifiers?id=${d.id}`); reload(); }}>Remove</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Compliance (opt-outs) ---------- */
function Compliance() {
  const { data, loading, reload } = useAsync(() => apiGet<{ optOuts: any[] }>('/api/optouts'), []);
  const [f, setF] = useState({ email: '', phone: '', channel: 'all' });
  if (loading) return <Loading />;
  return (
    <div className="space-y-4">
      <Alert kind="info">Anyone here is blocked from calls/emails automatically (TCPA / CAN-SPAM). The block is enforced on every send — not just shown.</Alert>
      <form onSubmit={async (e) => { e.preventDefault(); if (!f.email && !f.phone) return; await apiPost('/api/optouts', f); setF({ email: '', phone: '', channel: 'all' }); reload(); }}
        className="card flex flex-wrap items-end gap-3 p-4">
        <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <Field label="Block"><select className="input" value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}><option value="all">All outreach</option><option value="email">Email</option><option value="call">Calls</option><option value="sms">SMS</option></select></Field>
        <Button type="submit">Suppress</Button>
      </form>
      {(data?.optOuts || []).length === 0 ? (
        <EmptyState title="Suppression list is empty" body="Add anyone who's opted out. They'll be blocked everywhere." />
      ) : (
        <div className="card divide-y divide-line">
          {data!.optOuts.map((o) => (
            <div key={o.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="flex-1 truncate text-sm text-soft">{o.email || o.phone}</span>
              <Badge className="border-line text-muted">{o.channel}</Badge>
              <button className="text-xs text-danger hover:underline" onClick={async () => { await apiDelete(`/api/optouts?id=${o.id}`); reload(); }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Preferences (per-user) ---------- */
function Preferences() {
  const { data, loading } = useAsync(() => apiGet<{ prefs: any }>('/api/settings/prefs'), []);
  const [saved, setSaved] = useState(false);
  const [f, setF] = useState<any>(null);
  if (loading) return <Loading />;
  const p = f || data?.prefs || {};
  const set = (patch: any) => setF({ ...p, ...patch });
  async function save(e: FormEvent) {
    e.preventDefault();
    await apiPut('/api/settings/prefs', p);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }
  return (
    <form onSubmit={save} className="max-w-md space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Call window start"><Input type="time" value={p.callWindowStart || ''} onChange={(e) => set({ callWindowStart: e.target.value })} /></Field>
        <Field label="Call window end"><Input type="time" value={p.callWindowEnd || ''} onChange={(e) => set({ callWindowEnd: e.target.value })} /></Field>
      </div>
      <Field label="Email signature"><textarea className="input min-h-[80px]" value={p.emailSignature || ''} onChange={(e) => set({ emailSignature: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm text-soft">
        <input type="checkbox" checked={p.notifyDigest !== false} onChange={(e) => set({ notifyDigest: e.target.checked })} />
        Send me the weekly digest
      </label>
      <div className="flex items-center gap-3"><Button type="submit">Save</Button>{saved && <span className="text-sm text-success">Saved</span>}</div>
    </form>
  );
}

/* ---------- Research rubrics (Phase 5) ---------- */
interface Factor { name: string; description: string; good: string; bad: string }
interface RubricT { id: number; industryTag: string | null; factors: Factor[]; version: number; generatedAt: string; lastEditedAt: string | null }

function ResearchRubrics() {
  const { me } = useAuth();
  const canEdit = me?.role === 'owner' || me?.role === 'manager';
  const { data, loading, reload } = useAsync(
    () => apiGet<{ rubrics: RubricT[]; industries: { name: string; notes: string }[]; hasGeneral: boolean }>('/api/settings/rubrics'),
    []
  );
  const [gen, setGen] = useState<string | null>(null);
  if (loading) return <Loading />;

  const rubrics = data?.rubrics || [];
  const industries = data?.industries || [];
  const haveTags = new Set(rubrics.map((r) => r.industryTag).filter(Boolean) as string[]);
  const missing = industries.filter((i) => !haveTags.has(i.name));

  async function generate(tag: string | null) {
    setGen(tag || '__g__');
    try { await apiPost('/api/settings/rubrics', { industryTag: tag }); await reload(); }
    finally { setGen(null); }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Deep Research grades each prospect against custom fit factors — specific to what you sell, and to each vertical you target.
        Generated once and reused, so every prospect is judged on the same yardstick. Regenerate when your business or ideal customer changes.
      </p>

      {rubrics.length === 0 && (
        <EmptyState title="No rubrics yet" body={industries.length ? 'Generate one below — or it builds automatically the first time you run Deep Research.' : 'Add industries in Targeting first (or generate an account-general rubric below).'} />
      )}

      {rubrics.map((r) => (
        <RubricCard key={`${r.id}-${r.version}-${r.lastEditedAt || ''}`} rubric={r} canEdit={canEdit} regenerating={gen === (r.industryTag || '__g__')} onRegenerate={() => generate(r.industryTag)} onSaved={reload} />
      ))}

      {canEdit && (!data?.hasGeneral || missing.length > 0) && (
        <div className="card p-4">
          <div className="font-head text-sm font-semibold text-soft">Generate a rubric</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {!data?.hasGeneral && <Button variant="ghost" loading={gen === '__g__'} onClick={() => generate(null)}>Account-general</Button>}
            {missing.map((i) => <Button key={i.name} variant="ghost" loading={gen === i.name} onClick={() => generate(i.name)}>{i.name}</Button>)}
          </div>
        </div>
      )}
    </div>
  );
}

function RubricCard({ rubric, canEdit, regenerating, onRegenerate, onSaved }: { rubric: RubricT; canEdit: boolean; regenerating: boolean; onRegenerate: () => void; onSaved: () => void }) {
  const [factors, setFactors] = useState<Factor[]>(rubric.factors);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = JSON.stringify(factors) !== JSON.stringify(rubric.factors);
  const setF = (i: number, patch: Partial<Factor>) => setFactors(factors.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  async function save() {
    setSaving(true);
    try { await apiPatch(`/api/settings/rubrics/${rubric.id}`, { factors }); setSaved(true); setTimeout(() => setSaved(false), 1500); onSaved(); }
    finally { setSaving(false); }
  }

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-head font-semibold text-soft">{rubric.industryTag || 'Account-general'}</span>
          <Badge className="border-line text-muted">v{rubric.version}</Badge>
        </div>
        {canEdit && <Button variant="ghost" loading={regenerating} onClick={onRegenerate} className="px-3 py-1.5 text-sm">Regenerate</Button>}
      </div>

      <div className="mt-3 space-y-2">
        {factors.map((f, i) => (
          <div key={i} className="rounded-xl border border-line bg-surface-2 px-3 py-2.5">
            {canEdit ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <input className="input py-1.5 font-head font-semibold" value={f.name} onChange={(e) => setF(i, { name: e.target.value })} placeholder="Factor name" />
                  <button onClick={() => setFactors(factors.filter((_, idx) => idx !== i))} className="shrink-0 text-muted hover:text-danger" aria-label="Remove factor">×</button>
                </div>
                <textarea className="input min-h-[44px] py-1.5 text-sm" value={f.description} onChange={(e) => setF(i, { description: e.target.value })} placeholder="What this factor evaluates and why it matters" />
                <div className="grid gap-1.5 sm:grid-cols-2">
                  <input className="input py-1.5 text-sm" value={f.good} onChange={(e) => setF(i, { good: e.target.value })} placeholder="Strong prospect looks like…" />
                  <input className="input py-1.5 text-sm" value={f.bad} onChange={(e) => setF(i, { bad: e.target.value })} placeholder="Weak prospect looks like…" />
                </div>
              </div>
            ) : (
              <div>
                <div className="font-head font-semibold text-soft">{f.name}</div>
                <div className="mt-0.5 text-sm text-muted">{f.description}</div>
                <div className="mt-1 text-xs text-muted"><span className="text-success">Strong:</span> {f.good} · <span className="text-danger">Weak:</span> {f.bad}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          <Button variant="ghost" onClick={() => setFactors([...factors, { name: '', description: '', good: '', bad: '' }])} className="px-3 py-1.5 text-sm">+ Factor</Button>
          <Button onClick={save} loading={saving} disabled={!dirty} className="px-3 py-1.5 text-sm">Save edits</Button>
          {saved && <span className="text-sm text-success">Saved</span>}
        </div>
      )}
    </div>
  );
}
